import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { traderService } from '../services/traderService';
import { useAuth } from '../contexts/AuthContext';
import { 
  Loader2, 
  ArrowLeft, 
  Building2, 
  CheckCircle, 
  BarChart3, 
  Image, 
  MessageSquare, 
  Star, 
  Shield,
  Users,
  Mail
} from 'lucide-react';

/**
 * ClaimBusinessCTA - Landing page showing benefits of claiming a business
 * Routes users to sign up or login before proceeding to the actual claim process
 */
const ClaimBusinessCTA: React.FC = () => {
  const { traderId } = useParams<{ traderId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [trader, setTrader] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Load trader data to display business info
  useEffect(() => {
    const fetchTrader = async () => {
      if (!traderId) {
        setError('No business ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const traderData = await traderService.getTraderById(traderId);
        
        if (!traderData) {
          setError('Business not found');
          setLoading(false);
          return;
        }

        // Check if business is actually unclaimed
        if (!traderData.mockup || traderData.user_id) {
          setError('This business has already been claimed');
          setLoading(false);
          return;
        }

        setTrader(traderData);
      } catch (err) {
        console.error('Error fetching trader:', err);
        setError('Failed to load business details');
      } finally {
        setLoading(false);
      }
    };

    fetchTrader();
  }, [traderId]);

  // Check if email is a fake/placeholder email
  const isFakeEmail = trader?.email?.endsWith('@unclaimed.ballymarket.local') || false;
  
  // Check if email is a Google domain (gmail.com, googlemail.com)
  const isGoogleEmail = trader?.email?.toLowerCase().endsWith('@gmail.com') || 
                        trader?.email?.toLowerCase().endsWith('@googlemail.com') || false;

  // If user is already logged in, redirect to claim process directly
  const handleClaimClick = () => {
    if (user) {
      // User is logged in, go directly to claim process
      navigate(`/claim-business/${traderId}`);
    } else {
      // User needs to register/login first
      navigate('/register/user', { 
        state: { 
          returnTo: `/claim-business/${traderId}`,
          message: 'Create an account to claim your business'
        } 
      });
    }
  };

  // Benefits of claiming a business
  const benefits = [
    {
      icon: Shield,
      title: 'Verified Business Profile',
      description: 'Get a verified badge that builds trust with potential customers'
    },
    {
      icon: Image,
      title: 'Showcase Your Work',
      description: 'Add photos, featured offerings, and highlight your best services'
    },
    {
      icon: BarChart3,
      title: 'Business Analytics',
      description: 'Track profile views and see how customers find your business'
    },
    {
      icon: MessageSquare,
      title: 'Direct Contact',
      description: 'Customers can reach you directly through your profile'
    },
    {
      icon: Star,
      title: 'Build Reputation',
      description: 'Collect reviews and ratings from satisfied customers'
    },
    {
      icon: Users,
      title: 'Grow Your Network',
      description: 'Connect with other local businesses and expand your reach'
    }
  ];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading business details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !trader) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Claim</h1>
            <p className="text-gray-600 mb-6">{error || 'Business not found'}</p>
            <Link
              to="/trades"
              className="inline-flex items-center text-blue-600 hover:text-blue-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Traders
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-blue-100 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>

          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl font-bold mb-4">
              Is This Your Business?
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Claim your listing on BallyMarket and take control of your online presence
            </p>
          </div>
        </div>
      </div>

      {/* Business Card */}
      <div className="max-w-5xl mx-auto px-4 -mt-8">
        <div className="bg-white rounded-xl shadow-lg p-6 flex flex-col sm:flex-row items-center gap-6">
          {trader.avatar_url ? (
            <img
              src={trader.avatar_url}
              alt={trader.store_name}
              className="w-24 h-24 rounded-xl object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-12 h-12 text-gray-400" />
            </div>
          )}
          <div className="text-center sm:text-left flex-1">
            <h2 className="text-2xl font-bold text-gray-900">{trader.store_name}</h2>
            <p className="text-gray-600">{trader.location}</p>
            {trader.profession?.name && (
              <span className="inline-block mt-2 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                {trader.profession.name}
              </span>
            )}
          </div>
          <div className="flex-shrink-0">
            <span className="inline-flex items-center px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
              <CheckCircle className="w-4 h-4 mr-2" />
              Available to Claim
            </span>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-4">
          Why Claim Your Business?
        </h2>
        <p className="text-gray-600 text-center max-w-2xl mx-auto mb-12">
          Take ownership of your business profile and unlock powerful features to grow your presence in the local community.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <benefit.icon className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {benefit.title}
              </h3>
              <p className="text-gray-600 text-sm">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-12 sm:py-16 text-center">
          {isFakeEmail ? (
            // Business has no real email - show contact us message
            <>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Contact Us to Claim This Business
              </h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto">
                We don't have verified contact details for this business on file. 
                To claim this listing, please contact us directly with proof of ownership.
              </p>

              <a
                href={`mailto:info@ballymarket.ie?subject=Business%20Claim%20Request%20-%20${encodeURIComponent(trader?.store_name || '')}`}
                className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20"
              >
                <Mail className="w-5 h-5 mr-2" />
                Contact Us to Claim
              </a>

              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg max-w-md mx-auto">
                <p className="text-sm text-amber-800">
                  <strong>What to include:</strong> Business name, your name, role, and any documentation proving ownership (e.g., business registration, utility bill).
                </p>
              </div>

              <p className="mt-6 text-sm text-gray-500">
                Email us at <strong>info@ballymarket.ie</strong>
              </p>
            </>
          ) : (
            // Business has real email - show normal claim flow
            <>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Ready to Claim Your Business?
              </h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto">
                {user 
                  ? "You're logged in! Click below to start the verification process."
                  : "Create a free account to verify your ownership and start managing your business profile."
                }
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {isGoogleEmail && !user ? (
                  // Show Google sign-up option for Gmail users
                  <button
                    onClick={handleClaimClick}
                    className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors border border-gray-300 shadow-sm"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Sign up with Google ({trader?.email})
                  </button>
                ) : (
                  <button
                    onClick={handleClaimClick}
                    className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20"
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    {user ? 'Claim This Business' : 'Sign Up & Claim Business'}
                  </button>
                )}

                {!user && (
                  <Link
                    to="/login"
                    state={{ returnTo: `/claim-business/${traderId}` }}
                    className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors border border-gray-300"
                  >
                    Already have an account? Log In
                  </Link>
                )}
              </div>

              <p className="mt-6 text-sm text-gray-500">
                By claiming this business, you confirm that you are the owner or authorized representative.
              </p>
            </>
          )}
        </div>
      </div>

      {/* How It Works - Only show for businesses with real email */}
      {!isFakeEmail && (
        <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-12">
            How It Works
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {isGoogleEmail ? 'Sign in with Google' : 'Create Account'}
              </h3>
              <p className="text-gray-600 text-sm">
                {isGoogleEmail 
                  ? `Use your Google account (${trader?.email}) to sign up`
                  : 'Sign up with your email address to get started'
                }
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Verify Ownership</h3>
              <p className="text-gray-600 text-sm">
                {isGoogleEmail 
                  ? 'Your Google account verifies your identity automatically'
                  : 'Confirm your email to verify you own this business'
                }
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Manage Profile</h3>
              <p className="text-gray-600 text-sm">
                Update your info, add photos, and grow your business
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimBusinessCTA;
